import React, { useContext, useState } from "react";
import {
  ArrowTopRightOnSquareIcon,
  EllipsisVerticalIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import { formatDistanceToNow } from "date-fns";
import { UserContext } from "contexts/UserContext";
import UsernameDisplayField from "components/UsernameDisplayField";
import type { Environment } from "components/Environment/utils/types";
import {
  getEnvironmentStatus,
  getIngressApplicationsFromEnvironment,
  parseTimestamp,
} from "components/Environment/utils/environtmentUtils";
import {
  getCurrentUserAccessIdentities,
  userCanAccessSecureLink,
} from "components/Environment/utils/secureLinkAccess";
import { WorkspaceStatus } from "models/Workspace.model";
import { useEnvironmentNetworkAccess } from "hooks/environment/useEnvironmentNetworkAccess";
import type { DisplayPort } from "utils/environment/networkAccessFallback";
import { ShareSecureLinkDialog } from "components/NodeDetail/BrevConnect/NodeSecureLinksSection";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/Graphs/Dropdown";

type EnvironmentApplication = ReturnType<
  typeof getIngressApplicationsFromEnvironment
>[number];
export type ApplicationListItem = {
  application: EnvironmentApplication;
  environment: Environment;
};
type ApplicationRowState = "active" | "inaccessible" | "inactive";

const ApplicationAccessMenu: React.FC<{
  onSubmit: (
    authorizedEmails: string[],
    allowPublicUnauthenticated: boolean,
  ) => Promise<void>;
  port: DisplayPort;
}> = ({ onSubmit, port }) => {
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Application actions"
            className="inline-flex h-7 w-7 items-center justify-center rounded text-[#707070] transition hover:bg-white/[.04] hover:text-white"
          >
            <EllipsisVerticalIcon className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end">
          <DropdownMenuItem onSelect={() => setShareDialogOpen(true)}>
            Edit Access
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ShareSecureLinkDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        port={port}
        onSubmit={onSubmit}
      />
    </>
  );
};

const ApplicationTableRow: React.FC<{
  activeOrgId: string;
  item: ApplicationListItem;
  onOpenEnvironment: (path: string) => void;
  sectionLabel?: string;
  state: ApplicationRowState;
}> = ({ activeOrgId, item, onOpenEnvironment, sectionLabel, state }) => {
  const { application, environment } = item;
  const { me } = useContext(UserContext);
  const { httpPorts, resolveCtaHostname, setHTTPPortAccess, canManagePort } =
    useEnvironmentNetworkAccess(environment);
  const isSkybridge = environment.labels?.["tunnel-provider"] === "skybridge";
  const hostname = isSkybridge
    ? resolveCtaHostname(application)
    : application.hostname;
  const port = httpPorts.find((httpPort) => httpPort.hostname === hostname);
  const activeUserIdentities = getCurrentUserAccessIdentities(me);
  const userCanAccessLink =
    !port || userCanAccessSecureLink(port, activeUserIdentities);

  if (!hostname) return null;

  const inactive = state === "inactive";
  const href = hostname.startsWith("http") ? hostname : `https://${hostname}`;
  const createdAt = parseTimestamp(environment.create_time);
  const linkIsInaccessible = state === "inaccessible" || !userCanAccessLink;
  const canEditAccess =
    port !== undefined &&
    canManagePort(port) &&
    getEnvironmentStatus(environment) !== WorkspaceStatus.Deleting;

  return (
    <>
      {sectionLabel ? (
        <tr className="border-y border-[#1e1e1e] bg-black/[.12]">
          <td
            colSpan={6}
            className="px-4 py-1.5 text-[9.5px] font-bold uppercase tracking-[.08em] text-[#707070]"
          >
            {sectionLabel}
          </td>
        </tr>
      ) : null}
      <tr
        className={`border-b border-[#1e1e1e] transition-colors ${inactive ? "opacity-[.38]" : "hover:bg-white/[.025]"}`}
      >
        <td className="whitespace-nowrap px-4 py-2.5 text-[13px] font-semibold text-[#e8e8e8]">
          {application.name}
        </td>
        <td className="max-w-[300px] px-4 py-2.5">
          {linkIsInaccessible ? (
            <span
              title="You do not have access to this application."
              className="inline-flex max-w-full cursor-not-allowed items-center gap-1 truncate font-mono text-[11px] text-[#707070]"
            >
              <LockClosedIcon className="h-[9px] w-[9px] shrink-0" />
              <span className="truncate">{hostname}</span>
            </span>
          ) : !inactive ? (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              onClick={(event) => event.stopPropagation()}
              className="inline-flex max-w-full items-center gap-1 truncate font-mono text-[11px] text-highlight"
            >
              <LockClosedIcon className="h-[9px] w-[9px] shrink-0" />
              <span className="truncate">{hostname}</span>
              <ArrowTopRightOnSquareIcon className="h-[9px] w-[9px] shrink-0 opacity-70" />
            </a>
          ) : (
            <span className="text-[11px] text-[#707070]">—</span>
          )}
        </td>
        <td className="whitespace-nowrap px-4 py-2.5">
          <button
            type="button"
            onClick={() =>
              onOpenEnvironment(
                `/org/${activeOrgId}/environments/${environment.workspaceId}`,
              )
            }
            className="inline-flex items-center gap-1.5 text-[11.5px] text-secondary transition hover:text-white"
          >
            {environment.name}
          </button>
        </td>
        <td className="whitespace-nowrap px-4 py-2.5 font-mono text-[11px] text-[#707070]">
          {createdAt ? `${formatDistanceToNow(createdAt)} ago` : "—"}
        </td>
        <td className="whitespace-nowrap px-4 py-2.5 text-xs text-secondary">
          {environment.labels?.userId ? (
            <UsernameDisplayField
              userId={environment.labels.userId}
              className="text-xs text-secondary"
            />
          ) : (
            "Unknown"
          )}
        </td>
        <td className="whitespace-nowrap px-4 py-2.5 text-right">
          <div className="flex items-center justify-end gap-2">
            {!linkIsInaccessible && canEditAccess && port ? (
              <ApplicationAccessMenu
                port={port}
                onSubmit={(authorizedEmails, allowPublicUnauthenticated) =>
                  setHTTPPortAccess(
                    port.portId,
                    authorizedEmails,
                    allowPublicUnauthenticated,
                  )
                }
              />
            ) : null}
          </div>
        </td>
      </tr>
    </>
  );
};

export default ApplicationTableRow;
